import { Location } from '@angular/common';
import { Injectable } from '@angular/core';

import { IBet } from '@frontend/oxygen/shared-utils';
import { IMarket } from '@frontend/oxygen/shared-utils';
import { ISportEvent } from '@frontend/oxygen/shared-utils';
import { BehaviorSubject } from 'rxjs';
import * as _ from 'underscore';

import { IBetSelection } from '../../../betslip/services/betSelection/bet-selection.model';
import { SportTabsService } from '../../../sb/services/sportTabs/sport-tabs.service';
import { ITab } from '../../../shared/components/tabsPanel/tabs-panel.model';
import { GOLF_CONSTANTS } from '../../../shared/constants/channel.constant';
import { RoutingState } from '../../../shared/services/routingState/routing-state.service';
import { IVirtualSportEventEntity } from '../../../vsbr/models/virtual-sports-event-entity.model';
import { ISwitcherConfig } from '../../models/switcher-config.model';
import { GtmService } from '../gtm/gtm.service';
import { GamingService } from '../sport/gaming.service';
import { StorageService } from '../storage/storage.service';
import { WindowRefService } from '../windowRef/window-ref.service';
import { IGtmBetPosition, IGtmOrigin } from './models/gtm-origin.model';

@Injectable({ providedIn: 'root' })
export class GtmTrackingService {
    private locationPrefix: string;
    public location: string;
    private subLocation: string;
    private module: string;
    public gtmObject: IGtmOrigin;
    private placedBetsOrigins: { [key: string]: any };
    public activeTab: any;
    dimensionValue = new BehaviorSubject<boolean>(false);
    moduleLabel: string;
    constructor(
        private locationService: Location,
        private sportTabsService: SportTabsService,
        private storageService: StorageService,
        private gamingService: GamingService,
        private windowRefService: WindowRefService,
        private gtmService: GtmService,
        private routingState: RoutingState,
    ) {}

    /**
     * Get location and module for GTM
     *
     * @returns {object | null}
     */
    getTracking(): IGtmOrigin | null {
        if (this.gtmObject && this.gtmObject.location) {
            this.gtmObject.location = this.gtmObject.location === 'NOW & NEXT' ? 'Fanzone' : this.gtmObject.location;
        }
        return this.gtmObject;
    }

    /**
     * Restores trace object of bet (eg after page refresh)
     *
     * @param {IGtmOrigin} betTrace
     */
    restoreTracking(betTrace: IGtmOrigin): void {
        if (betTrace) {
            this.gtmObject = betTrace;
        }
    }

    /**
     * Detects liocation and module for GTM
     * @param {string} moduleTitle - current GTM module
     * @param {string} segment - current route segment
     * @param {ISportEvent} event -  event of bet wich was added
     * @param {IMarket} market -  market of bet wich was added
     * @returns {object}
     */
    detectTracking(
        moduleTitle: string,
        segment: string,
        event: ISportEvent,
        market: IMarket,
        placedLocation?: string,
        positionObj?: IGtmBetPosition,
    ): IGtmOrigin | null {
        this.module = moduleTitle || '';
        this.locationPrefix = '';

        if (!this.module) {
            const segmentParts = segment.split('.');
            // event page
            if (segmentParts.some((el) => el === 'eventMain')) {
                this.module = 'edp';
            }

            // Sport matches tab
            if (segment === 'sport.matches.tab') {
                const section = this.sportTabsService.eventsBySections([event], this.gamingService);
                this.module = section.length ? (section[0].sectionTitle ?? '') : '';
            }
        }

        const currentPath = this.locationService.path().toLowerCase();
        // Home page prefix
        if (currentPath === '' || currentPath.indexOf('/home') === 0) {
            this.locationPrefix = 'HOME';
        }

        // If location was not detected - no switchers on page which set location/sublocation
        if (!this.location) {
            switch (true) {
                case ['horseracing.eventMain', 'greyhound.eventMain'].includes(segment):
                    this.location = event.localTime ?? '';
                    break;
                case segment === 'eventMain':
                    this.location = event.categoryName;
                    break;
                case segment === 'favourites':
                    this.location = 'FAVOURITES';
                    break;
            }
        }

        if (['horseracing.eventMain', 'greyhound.eventMain'].includes(segment)) {
            this.location = event.localTime ?? '';
        }

        let location = [this.locationPrefix, this.location, this.subLocation];
        location = _.filter(location, (el) => Boolean(el));
        if (placedLocation) {
            location = [placedLocation];
        }
        this.gtmObject = {
            location: location.join('. '),
            module: this.module,
            betPosition: positionObj,
        };
        return this.getTracking();
    }

    detectVirtualSportTracking(module: string, virtualSportEvent: IVirtualSportEventEntity): IGtmOrigin | null {
        this.gtmObject = {
            location: virtualSportEvent.title,
            module: module,
        };
        return this.getTracking();
    }

    /**
     * Set location and sublocation values according current switcher value and current route
     * @param {string} name - new location/sublocation value
     * @param {string} type - 'location' or 'sublocation'
     */
    setLocation(name: string, type: string): void {
        if (type === 'location') {
            this.location = name && name.toUpperCase();
        }
        if (type === 'sublocation') {
            this.subLocation = name.toUpperCase();
        }
    }

    /**
     * Clear current locatio & sublocation when switcher is destroyed
     * @param {string} type - 'location' or 'sublocation'
     * @return void
     */
    clearLocation(type: string): void {
        if (type === 'location') {
            this.location = '';
        }
        if (type === 'sublocation') {
            this.subLocation = '';
        }
    }

    /**
     * Collect placed bet origins
     * @param {IBet[]} bets
     * @return void
     */
    collectPlacedBets(bets: IBet[]): void {
        this.placedBetsOrigins = {};
        const selections = <IBetSelection[]>this.storageService.get('betSelections');
        selections.forEach((betSelection: IBetSelection) => {
            if (betSelection.outcomesIds?.length && betSelection.GTMObject) {
                this.placedBetsOrigins[betSelection.outcomesIds[0]] = betSelection.GTMObject.tracking;
            }
        });
    }

    /**
     * Get collected bets origins by outcomeId
     * @param {string} outcomeId
     * @return IGtmOrigin
     */
    getBetOrigin(outcomeId: any): IGtmOrigin {
        const origin: IGtmOrigin = {
            location: '',
            module: '',
            betType: '',
            betPosition: {},
        };
        const reuseBetOrigin = <IGtmOrigin[]>this.storageService.get('reuseBetSelections');
        if (reuseBetOrigin && reuseBetOrigin[outcomeId]) {
            origin.location = reuseBetOrigin[outcomeId].location;
            origin.module = reuseBetOrigin[outcomeId].module;
            origin.betType = reuseBetOrigin[outcomeId].betType;
            origin.betPosition = reuseBetOrigin[outcomeId].betPosition;
        } else if (this.placedBetsOrigins && this.placedBetsOrigins[outcomeId]) {
            origin.location = this.placedBetsOrigins[outcomeId].location === 'NOW & NEXT' ? 'Fanzone' : this.placedBetsOrigins[outcomeId].location;
            origin.module = this.placedBetsOrigins[outcomeId].module;
            origin.betType = this.placedBetsOrigins[outcomeId].betType;
            origin.betPosition = this.placedBetsOrigins[outcomeId].betPosition;
        }
        return origin;
    }

    /**
     * Restore bet GTM origin after reuse selection
     * @param {string[]} outcomesIds - outcomes which were reused
     * @return void
     */
    restoreGtmTracking(outcomesIds: string[]): void {
        const selections = <IBetSelection[]>this.storageService.get('betSelections');
        _.each(selections, (betSelection: IBetSelection) => {
            if (betSelection && Array.isArray(betSelection.outcomesIds) && betSelection.outcomesIds.length === 1 && betSelection.GTMObject) {
                const outcomeId = betSelection.outcomesIds[0];
                if (this.placedBetsOrigins && this.placedBetsOrigins[outcomeId] && outcomesIds.includes(outcomeId)) {
                    betSelection.GTMObject.tracking = this.placedBetsOrigins[outcomeId];
                }
            }
        });
        this.storageService.set('betSelections', selections);
    }

    set activeTabData(data) {
        this.activeTab = data;
    }
    get activeTabData() {
        return this.activeTab;
    }

    sendGTMDataOnTabClick(selectedTab: ITab | ISwitcherConfig, sportName?: string, preBuilt?: string) {
        let modifiedTabLabel = selectedTab.label ?? '';

        if (preBuilt && modifiedTabLabel) {
            modifiedTabLabel = modifiedTabLabel.replace(/\s*\(\d+\)$/, '');
        }

        let locationEvent = this.routingState.getPreviousUrl();
        let eventDetails = modifiedTabLabel;
        let urlClicked = this.windowRefService.nativeWindow.location.href;

        if (sportName === 'golf' && 'id' in selectedTab && selectedTab.id in GOLF_CONSTANTS) {
            const tabId = selectedTab.id as keyof typeof GOLF_CONSTANTS;
            locationEvent = GOLF_CONSTANTS[tabId];
            eventDetails = GOLF_CONSTANTS[tabId];
            urlClicked = 'Clicked URL';
        }

        const gtmData = {
            'event': 'Event.Tracking',
            'component.CategoryEvent': 'navigation',
            'component.LabelEvent': sportName === 'golf' ? 'golf' : 'edp sub nav',
            'component.ActionEvent': 'click',
            'component.PositionEvent': preBuilt ? 'bet builder tab' : 'not applicable',
            'component.LocationEvent': locationEvent,
            'component.EventDetails': eventDetails,
            'component.URLClicked': urlClicked,
        };

        this.gtmService.push(gtmData.event, gtmData);
    }

    set findModule(module: string) {
        this.moduleLabel = module;
    }
    get findModule() {
        return this.moduleLabel;
    }
}
